<script setup lang="ts">
import { ref, watch, inject } from 'vue'
import CreateOrderModal, { type CreateOrderPayload } from './CreateOrderModal.vue'
import ToastComponent from '@/components/ToastComponent.vue'
import { usePopupAction } from '@/composables/usePopupAction'
import { TOAST_DURATION } from '@/constants/toast'
import {
  postCareOrderMeta,
  postCareOrderList,
  postCareOrderCreate,
  parseCareOrderListResponse,
  type CareOrderMetaData,
  type OrderCardDTO,
} from '@/api/orders'

const open = defineModel<boolean>('open', { default: false })
const { pending: creating, run: runCreate } = usePopupAction()

const emit = defineEmits<{
  refresh: []
  'local-insert': []
}>()

const careMeta = ref<CareOrderMetaData | null>(null)
/** 仅 session 本地未同步订单 ID，不再打开弹窗时分页预拉 list */
const existingOrderIds = ref<string[]>([])

const HM_CARE_LOCAL_ORDERS_KEY = 'hmCareOrderLocalOrders'

function addOrdersToIdSet(list: OrderCardDTO[], orderIds: Set<string>): void {
  for (const o of list) {
    const id = String(o.id ?? '').trim()
    const pk = o.orderPrimaryId
    if (id) orderIds.add(id)
    if (pk != null && Number.isFinite(pk)) {
      orderIds.add(String(pk))
      orderIds.add(`HM-${pk}`)
    }
  }
}

async function orderIdExistsInApi(orderId: string): Promise<boolean> {
  const key = orderId.trim().toLowerCase()
  if (!key) return false
  try {
    const raw = await postCareOrderList({
      page: 1,
      pageSize: 20,
      searchField: 'order_id',
      searchQuery: orderId.trim(),
    })
    const parsed = parseCareOrderListResponse(raw)
    if (!parsed?.list.length) return false
    return parsed.list.some((o) => {
      const id = String(o.id ?? '').trim().toLowerCase()
      const pk = o.orderPrimaryId
      if (id === key) return true
      if (pk != null && (String(pk).toLowerCase() === key || `hm-${pk}`.toLowerCase() === key)) {
        return true
      }
      return false
    })
  } catch {
    return false
  }
}

function readLocalCareOrdersFromSession(): OrderCardDTO[] {
  const raw = sessionStorage.getItem(HM_CARE_LOCAL_ORDERS_KEY)
  if (!raw) return []
  try {
    const parsed = JSON.parse(raw) as unknown
    if (!Array.isArray(parsed)) return []
    return parsed.filter(
      (x): x is OrderCardDTO =>
        !!x &&
        typeof x === 'object' &&
        typeof (x as OrderCardDTO).id === 'string' &&
        Array.isArray((x as OrderCardDTO).services),
    )
  } catch {
    return []
  }
}

function isDuplicateOrderId(orderId: string, ids: string[]): boolean {
  const oid = orderId.trim()
  if (!oid) return false
  const key = oid.toLowerCase()
  return ids.some((x) => String(x).trim().toLowerCase() === key)
}

/** 只同步本地 session 订单 ID，不打 list */
function loadLocalOrderIds(): void {
  const orderIds = new Set<string>()
  addOrdersToIdSet(readLocalCareOrdersFromSession(), orderIds)
  existingOrderIds.value = [...orderIds]
}

const toast = ref({
  visible: false,
  type: 'success' as 'success' | 'warning' | 'error',
  message: '',
})

const showToastInject = inject<(t: 'success' | 'warning' | 'error', m: string) => void>('showToast')

function showToast(type: 'success' | 'warning' | 'error', message: string) {
  if (showToastInject) {
    showToastInject(type, message)
    return
  }
  toast.value.type = type
  toast.value.message = message
  toast.value.visible = true
  window.setTimeout(() => {
    toast.value.visible = false
  }, type === 'success' ? TOAST_DURATION.brief : TOAST_DURATION.warning)
}

async function prepareDialog(): Promise<void> {
  try {
    careMeta.value = await postCareOrderMeta()
  } catch {
    careMeta.value = null
  }
  loadLocalOrderIds()
}

watch(
  open,
  (visible) => {
    if (visible) void prepareDialog()
  },
  { immediate: true },
)

function apiErrorMessage(e: unknown): string {
  if (typeof e === 'object' && e !== null && 'response' in e) {
    const r = (e as { response?: { data?: unknown; statusText?: string } }).response
    const d = r?.data
    if (typeof d === 'string' && d.trim()) return d
    if (typeof d === 'object' && d !== null && 'message' in d) {
      const m = (d as { message: unknown }).message
      if (typeof m === 'string' && m.trim()) return m
    }
    if (typeof r?.statusText === 'string' && r.statusText.trim()) return r.statusText
  }
  if (e instanceof Error && e.message) return e.message
  return 'Request failed. Please try again.'
}

function formatPlacedNow(): string {
  return new Date().toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
}

function payloadToOrderCard(p: CreateOrderPayload): OrderCardDTO {
  return {
    id: p.orderId,
    homeId: p.userId,
    status: 'Pending Schedule',
    serviceDate: p.serviceCycleDate,
    placementDate: formatPlacedNow(),
    notes: p.notes?.trim() || `Notes: ${p.contactName} · ${p.phone}`,
    services: [
      {
        serviceName: p.service,
        productCode: '—',
        scheduledAt: p.serviceCycleDate,
        address: p.serviceAddress,
        assignment: 'Pending Assignment',
        price: '—',
        paymentMethod: '—',
        canSchedule: true,
        canReschedule: false,
        canRefund: false,
        canMessage: true,
        canNote: true,
      },
    ],
  }
}

async function onCreated(payload: CreateOrderPayload) {
  if (creating.value) return
  await runCreate(async () => {
    loadLocalOrderIds()
    if (
      isDuplicateOrderId(payload.orderId, existingOrderIds.value) ||
      (await orderIdExistsInApi(payload.orderId))
    ) {
      showToast('warning', 'Order ID already exists')
      return
    }

    const canRemoteCreate =
      payload.planKey != null &&
      String(payload.planKey).trim() !== '' &&
      payload.careTemplateId != null &&
      !Number.isNaN(Number(payload.careTemplateId))

    if (canRemoteCreate) {
      try {
        await postCareOrderCreate({
          order_id: payload.orderId,
          user_id: payload.userId,
          plan_key: String(payload.planKey).trim(),
          care_template_id: Number(payload.careTemplateId),
          service_date: payload.serviceCycleDate,
          service_address: payload.serviceAddress,
          contact_name: payload.contactName,
          contact_phone: payload.phone,
          notes: payload.notes?.trim() || undefined,
        })
        showToast('success', 'Successfully saved')
        open.value = false
        window.setTimeout(() => emit('refresh'), 450)
        return
      } catch (e: unknown) {
        showToast('error', apiErrorMessage(e))
        return
      }
    }

    try {
      const card = payloadToOrderCard(payload)
      sessionStorage.setItem('hmCareOrderLocalInsert', JSON.stringify(card))
      const listKey = HM_CARE_LOCAL_ORDERS_KEY
      const prevRaw = sessionStorage.getItem(listKey)
      let list: OrderCardDTO[] = []
      if (prevRaw) {
        try {
          const parsed = JSON.parse(prevRaw) as unknown
          if (Array.isArray(parsed)) {
            list = parsed.filter(
              (x): x is OrderCardDTO =>
                !!x &&
                typeof x === 'object' &&
                typeof (x as OrderCardDTO).id === 'string' &&
                Array.isArray((x as OrderCardDTO).services),
            )
          }
        } catch {
          list = []
        }
      }
      list = [card, ...list.filter((o) => o.id !== card.id)]
      sessionStorage.setItem(listKey, JSON.stringify(list))
      open.value = false
      emit('local-insert')
    } catch {
      showToast('error', 'Could not return to orders list.')
    }
  })
}

function onCancel() {
  open.value = false
}
</script>

<template>
  <CreateOrderModal
    :open="open"
    :submitting="creating"
    :care-meta="careMeta"
    :existing-order-ids="existingOrderIds"
    :on-toast="showToast"
    :before-duplicate-check="loadLocalOrderIds"
    :check-order-id-exists="orderIdExistsInApi"
    @update:open="open = $event"
    @created="onCreated"
    @cancel="onCancel"
  />
  <Teleport to="body">
    <ToastComponent v-if="!showToastInject" v-bind="toast" />
  </Teleport>
</template>
